import { ShieldCheck, Wrench, Heart, Sparkles } from "lucide-react";
import { BRAND } from "@/lib/site";
import SEO from "@/components/SEO";
import PageHeader from "@/components/PageHeader";
import TrustBand from "@/components/TrustBand";
import CallStrip from "@/components/CallStrip";

const VALUES = [
  {
    icon: ShieldCheck,
    title: "Licensed & accountable",
    body: "Fully NSW-licensed plumbing & gas fitting. $20M public liability.",
  },
  {
    icon: Wrench,
    title: "Fix it once, properly",
    body: "We diagnose the root cause, use quality fittings, and back our work in writing.",
  },
  {
    icon: Heart,
    title: "Local & owner-led",
    body: "Real people from Blacktown answering the phone — no scripts, no upsells.",
  },
  {
    icon: Sparkles,
    title: "Tidy & respectful",
    body: "Drop sheets and a vacuum on every job. We leave your home cleaner than we found it.",
  },
];

export default function AboutPage() {
  return (
    <>
      <SEO
        title="About Us — Locally Owned Sydney Plumbers"
        description="About Flowgrid Plumbing — a Sydney Metro plumber built on trust. Licensed NSW plumbers & gas fitters, owner-led, honest pricing and workmanship guaranteed in writing."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "About Flowgrid Plumbing",
          mainEntity: {
            "@type": "Organization",
            name: BRAND.name,
            telephone: BRAND.phone,
            email: BRAND.email,
          },
        }}
      />
      <PageHeader
        eyebrow="About"
        title="A Sydney Metro plumber built on trust."
        subtitle="Flowgrid Plumbing started with a simple idea — a plumber that picks up the phone, turns up on time, and charges what was quoted."
      />

      <TrustBand />

      <section className="bg-white py-20 md:py-24">
        <div className="fg-container grid items-center gap-12 md:grid-cols-12">
          <div className="md:col-span-6">
            <img
              src={BRAND.vanUrl}
              alt="Flowgrid plumbing van"
              className="rounded-2xl border border-slate-200 bg-white p-3 shadow-lg"
              loading="lazy"
              data-testid="about-image"
            />
          </div>
          <div className="md:col-span-6">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700">
              Our story
            </div>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Locally owned. Honestly run.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600">
              At FlowGrid Plumbing, we provide premium plumbing services across
              Sydney, built on quality workmanship, reliability, and genuine
              customer care. With years of industry experience, we take pride
              in delivering practical, long-lasting solutions while making the
              process simple and stress-free for our clients.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              We believe great plumbing is about more than fixing problems —
              it&apos;s about building trust. That&apos;s why we focus on
              honest advice, clear communication, and workmanship completed to
              the highest standard. Whether it&apos;s a simple repair,
              emergency call-out, or major installation, you can rely on
              FlowGrid Plumbing to get the job done right.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20 md:py-24">
        <div className="fg-container">
          <div className="max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700">
              What we stand for
            </div>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Four promises we won't break.
            </h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v) => {
              const Icon = v.icon;
              return (
                <div
                  key={v.title}
                  className="fg-card-hover rounded-2xl border border-slate-200 bg-white p-7"
                  data-testid={`about-value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="mt-5 font-display text-lg font-bold text-slate-900">
                    {v.title}
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    {v.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <CallStrip />
    </>
  );
}
